import { Check, Clock, CreditCard } from "lucide-react";
import { cn } from "@/lib/utils";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";

interface PaymentProgressProps {
  seekerPaid?: boolean | null;
  otherPartyPaid?: boolean | null;
  seekerName?: string | null;
  otherPartyName?: string | null;
  /** Snapshot amount per payer in cents (from consultations.amount_paid_cents). */
  amountPaidCents?: number | null;
  /** Snapshot duration in minutes (from consultations.duration_min). */
  durationMin?: number | null;
  meetingNumber?: number | null;
  /** Compact = single-line layout for cards; default = vertical with progress bar. */
  variant?: "default" | "compact";
  className?: string;
}

function formatEur(cents?: number | null): string | null {
  if (cents == null || !Number.isFinite(cents) || cents <= 0) return null;
  return `${(cents / 100).toFixed(2).replace(".", ",")} €`;
}

export function PaymentProgress({
  seekerPaid,
  otherPartyPaid,
  seekerName,
  otherPartyName,
  amountPaidCents,
  durationMin,
  meetingNumber,
  variant = "default",
  className,
}: PaymentProgressProps) {
  const paidCount = (seekerPaid ? 1 : 0) + (otherPartyPaid ? 1 : 0);
  const percent = (paidCount / 2) * 100;
  const allPaid = paidCount === 2;
  const amountLabel = formatEur(amountPaidCents);

  const PayerRow = ({
    paid,
    name,
    fallback,
  }: {
    paid?: boolean | null;
    name?: string | null;
    fallback: string;
  }) => (
    <div className="flex items-center justify-between gap-2 text-sm">
      <div className="flex items-center gap-2 min-w-0">
        <span
          className={cn(
            "flex h-5 w-5 items-center justify-center rounded-full flex-shrink-0",
            paid ? "bg-green-100 text-green-700" : "bg-muted text-muted-foreground"
          )}
        >
          {paid ? <Check className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
        </span>
        <span className="truncate text-foreground">{name || fallback}</span>
      </div>
      <span
        className={cn(
          "text-xs font-medium flex-shrink-0",
          paid ? "text-green-700" : "text-muted-foreground"
        )}
      >
        {paid ? "Payé" : "En attente"}
      </span>
    </div>
  );

  if (variant === "compact") {
    return (
      <div className={cn("flex flex-col gap-1 text-xs", className)}>
        <div className="flex items-center justify-between">
          <span className="font-medium text-foreground">
            Paiements {paidCount}/2
          </span>
          {amountLabel && (
            <span className="text-muted-foreground">
              {amountLabel}/partie
              {durationMin ? ` · ${durationMin} min` : ""}
            </span>
          )}
        </div>
        <Progress value={percent} className="h-1.5" />
        <div className="flex flex-col gap-0.5 pt-1">
          <span className={seekerPaid ? "text-green-700" : "text-muted-foreground"}>
            {seekerPaid ? "✓" : "○"} {seekerName || "Demandeur"} :{" "}
            {seekerPaid ? "payé" : "en attente"}
          </span>
          <span className={otherPartyPaid ? "text-green-700" : "text-muted-foreground"}>
            {otherPartyPaid ? "✓" : "○"} {otherPartyName || "Autre partie"} :{" "}
            {otherPartyPaid ? "payé" : "en attente"}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "rounded-lg border bg-card p-4 space-y-3",
        allPaid && "border-green-200 bg-green-50/50",
        className
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <CreditCard className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold text-foreground">
            Avancement des paiements
          </h3>
        </div>
        <Badge
          variant={allPaid ? "default" : "secondary"}
          className={
            allPaid
              ? "bg-green-100 text-green-800 hover:bg-green-100"
              : undefined
          }
        >
          {paidCount}/2 payés
        </Badge>
      </div>

      <Progress value={percent} className="h-2" />

      <div className="space-y-2">
        <PayerRow paid={seekerPaid} name={seekerName} fallback="Demandeur" />
        <PayerRow
          paid={otherPartyPaid}
          name={otherPartyName}
          fallback="Autre partie"
        />
      </div>

      {(amountLabel || durationMin) && (
        <div className="flex items-center justify-between text-xs text-muted-foreground border-t pt-2">
          <span>
            {meetingNumber === 1
              ? "Premier RDV informatif"
              : meetingNumber
              ? `RDV de médiation${meetingNumber > 1 ? ` n°${meetingNumber}` : ""}`
              : "Rendez-vous"}
          </span>
          <span className="flex items-center gap-2">
            {amountLabel && <span className="font-medium text-foreground">{amountLabel}/partie</span>}
            {durationMin && (
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3" /> {durationMin} min
              </span>
            )}
          </span>
        </div>
      )}
    </div>
  );
}
